import { ArrowBack, CheckCircleOutline, LaunchOutlined } from '@mui/icons-material';
import { Box, Button, Container, Grid, Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useCaseData from './useCaseData';

const UseCasePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const uc = useCaseData.find((d) => d.slug === slug);

  if (!uc) {
    return (
      <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4">Use case not found.</Typography>
        <Button sx={{ mt: 3 }} onClick={() => navigate('/')}>Back to home</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* Hero */}
      <Box component="section" sx={{ bgcolor: '#172126', color: 'white' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 9 } }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, '&:hover': { color: 'white' } }}
          >
            Back
          </Button>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {uc.eyebrow}
          </Typography>
          <Typography variant="h2" sx={{ mt: 1, mb: 3, maxWidth: 820, fontSize: { xs: '2.2rem', md: '3.5rem' } }}>
            {uc.title}
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 760, color: 'rgba(255,255,255,0.82)', fontWeight: 400, lineHeight: 1.65 }}>
            {uc.intro}
          </Typography>
        </Container>
      </Box>

      {/* Bullets + Images */}
      <Box component="section" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 9 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="flex-start">

            {/* Bullets */}
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="secondary.dark">Key capabilities</Typography>
              <Typography variant="h4" sx={{ mt: 1, mb: 3 }}>What FEMORPH enables</Typography>
              <Stack spacing={2}>
                {uc.bullets.map((b, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckCircleOutline sx={{ color: 'primary.main', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {b.bold && <strong>{b.bold} </strong>}
                      {b.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>

            {/* Images */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {uc.images.map((img) => (
                  <Grid item xs={12} sm={uc.images.length === 1 ? 12 : 6} key={img.src}>
                    <Box
                      component="img"
                      src={img.src}
                      alt={img.caption ?? uc.title}
                      sx={{
                        width: '100%',
                        display: 'block',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: '#eef3f1',
                      }}
                    />
                    {img.caption && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}>
                        {img.caption}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* Description + CTA */}
      {(uc.description || uc.externalLink) && (
        <Box component="section" sx={{ bgcolor: '#eef3f1', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
            <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
              {uc.description && (
                <Grid item xs={12} md={uc.externalLink ? 8 : 12}>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.8, maxWidth: 820 }}>
                    {uc.description}
                  </Typography>
                </Grid>
              )}
              {uc.externalLink && (
                <Grid item xs={12} md={4}>
                  <Link
                    href={uc.externalLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                  >
                    <Button variant="outlined" endIcon={<LaunchOutlined />} size="large">
                      {uc.externalLink.label}
                    </Button>
                  </Link>
                </Grid>
              )}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Footer CTA */}
      <Box component="section" sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5">Ready to put FEMORPH to work?</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                Contact us to discuss how FEMORPH fits your workflow.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={() => navigate('/support')}>Contact us</Button>
              <Button variant="outlined" onClick={() => navigate('/')}>Explore more</Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
};

export default UseCasePage;
